import { toCloseLeadRef } from 'src/components/LeadClose/LeadClose.functions';
import { useLeadClose } from 'src/components/LeadClose/useLeadClose.hook';
import { useEffect, useRef, useState } from 'react';
import { NIL } from 'uuid';
import { useModalsContext } from '../../../../providers/ModalsProvider/Modals.context.ts';
import { eChannelType } from '../../../../providers/SocketsProvider/Sockets.enums.ts';
import { useSocketsContext } from '../../../../providers/SocketsProvider/Sockets.context.ts';
import {
  createArchiveFrameOrder,
  frameActiveChange,
} from '../../../../pages/MessengerV2/Messenger.functions.ts';
import useChatActions from '../../Chat.actions.ts';
import { useChatContext } from '../../Chat.hook.ts';
import useContactBlock from '../../useContactBlock.hook.ts';
import { tLeadStages } from '../../Chat.types.ts';
import { emitLeadActiveChanged } from '../../leadActiveChanged.event.ts';
import { useBoardSlice } from 'src/pages/Pipe/PipeBoard.context';
import {
  tUpdateStatusOptions,
  tUseChatMenu,
} from './ChatMenu.types.ts';

export default function useChatMenu(): tUseChatMenu {
  const { updateLead, archiveLead, unarchiveLead } = useChatActions();
  const leadClose = useLeadClose();
  const { selectedChat } = useChatContext();
  const { currentFunnel, moveLeadToStage, removeLeadFromFunnel } = useBoardSlice();
  const {
    isBlocked,
    loading: blockLoading,
    setContactBlocked,
  } = useContactBlock();
  const [isActive, setIsActive] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const { selectedLeadForChatModal, setSelectedLeadForChatModal, setTriggerDirectoryReload } =
    useModalsContext();

  const { leadUpdatedEvent } = useSocketsContext();
  const isStaleArchiveFrameRef = useRef(createArchiveFrameOrder());

  useEffect(() => {
    setIsActive(Boolean(selectedLeadForChatModal?.active));
  }, [selectedLeadForChatModal?.id, selectedLeadForChatModal?.active]);

  // BUG-1127: another user archiving or activating the open lead reaches this
  // tab as a `lead-updated` frame. The button reads the selected lead, so the
  // frame writes it there, only while that lead is still the one open. Chat.hook
  // counts the change, so its lead read in flight cannot undo it.
  leadUpdatedEvent.useSubscription((event) => {
    const change = frameActiveChange(event.response);
    if (!change) return;
    // Parallel consumers can deliver an older frame after a newer one. The
    // order is recorded for every lead, not only the open one: a lead opened
    // after its newer frame landed must still drop an older one.
    if (isStaleArchiveFrameRef.current(change)) return;
    if (change.id !== selectedLeadForChatModal?.id) return;
    setSelectedLeadForChatModal((prev) =>
      prev && prev.id === change.id && prev.active !== change.active
        ? { ...prev, active: change.active }
        : prev,
    );
  });

  // A chat switch must never leak a pending confirmation onto the next lead.
  useEffect(() => {
    setIsBlockModalOpen(false);
  }, [selectedChat?.id]);

  /**
   * Block toggle entry point (INT-1294). Blocking asks for confirmation first
   * (the contact stops being able to message the business); unblocking is
   * direct.
   */
  function handleToggleBlock() {
    if (isBlocked) {
      void setContactBlocked(false);
      return;
    }
    setIsBlockModalOpen(true);
  }

  async function handleConfirmBlock() {
    setIsBlockModalOpen(false);
    await setContactBlocked(true);
  }

  async function handleUpdateCountMessages(read: boolean) {
    const newValue = {
      message: {
        unread_messages: read ? 0 : 1,
        last_message: '',
        first_message: '',
        last_message_on: '',
        first_message_on: '',
        last_message_from_me: true,
        channel_type: eChannelType.WHATSAPP_WEB,
        read: read,
      },
    };
    setLoading(true);

    await updateLead(newValue);

    setLoading(false);
  }

  /**
   * BUG-1126: the single archive goes through Ragnarok, which sends no
   * `lead-updated` socket frame, so the chat list never heard of it. The list
   * is told directly, for the lead the request was for, even if another chat
   * is open by now. The selected lead (which this menu reads) is updated only
   * while it is still that lead.
   */
  function applyActive(leadId: string | undefined, active: boolean) {
    if (leadId) emitLeadActiveChanged({ id: leadId, active });
    setSelectedLeadForChatModal((prev) =>
      prev && prev.id === leadId ? { ...prev, active } : prev,
    );
    setTriggerDirectoryReload((prev) => !prev);
  }

  async function handleUpdateArchive() {
    const leadId = selectedLeadForChatModal?.id;
    setLoading(true);

    try {
      if (isActive) {
        // Lead is active, so we want to archive it
        const response = await archiveLead();
        if (response?.success) applyActive(leadId, false);
      } else {
        // Lead is archived, so we want to unarchive it
        const response = await unarchiveLead();
        if (response?.success) applyActive(leadId, true);
      }
    } catch (error) {
      // Error is already handled in the action, just log it here
      console.error('Archive operation failed:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleUpdateStage(stage: tLeadStages, funnelId: string) {
    const leadId = selectedLeadForChatModal?.id;
    const newValue =
      stage.id === NIL && funnelId === NIL
        ? {
            in_inbox: true,
          }
        : {
            stage_uuid: stage.id,
            funnel_uuid: funnelId,
            in_inbox: false,
          };
    setLoading(true);

    try {
      // LEA-50: a move into a stage mapped to a result opens its form (U16),
      // from the bar and from "Otro embudo" alike: one call per move.
      const saved = await updateLead(newValue, {
        leadId,
        onPrompt: (type) => {
          if (!selectedLeadForChatModal) return;
          leadClose.openForm({
            lead: { ...toCloseLeadRef(selectedLeadForChatModal), stageName: stage.name },
            type,
            source: 'stage_entry',
            surface: 'chat',
            deferAvailability: true,
          });
        },
      });
      if (!saved) return;
      if (leadId) {
        if (currentFunnel?.id === funnelId) moveLeadToStage(leadId, stage.id);
        else removeLeadFromFunnel(leadId);
      }
      setSelectedLeadForChatModal((prev) =>
        prev && prev.id === leadId
          ? { ...prev, stage: { ...prev.stage, id: stage.id, name: stage.name } }
          : prev,
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleUpdateStatus(options: tUpdateStatusOptions) {
    const newValue = {
      stage_uuid: options.stage.id,
      status_uuid: options.status.id,
      funnel_uuid: options.funnelId,
      in_inbox: false,
    };
    setLoading(true);

    await updateLead(newValue);

    setLoading(false);
  }

  return {
    handleUpdateCountMessages,
    handleUpdateArchive,
    handleUpdateStage,
    handleUpdateStatus,
    handleToggleBlock,
    handleConfirmBlock,
    isBlockModalOpen,
    setIsBlockModalOpen,
    isBlocked,
    loading: loading || blockLoading,
    setLoading,
    isActive,
  };
}
